import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import portraitManifest from '../voicePortraits.json';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Deterministic weekly pick — same voice for everyone all week, rotates Monday UTC
const weeklyVoiceId = () => {
  const ids = Object.keys(portraitManifest).sort();
  const msPerWeek = 7 * 24 * 60 * 60 * 1000;
  const weekIndex = Math.floor((Date.now() - Date.UTC(2026, 0, 5)) / msPerWeek); // anchor: Mon Jan 5 2026
  return ids[((weekIndex % ids.length) + ids.length) % ids.length];
};

const VoiceOfTheWeek = () => {
  const navigate = useNavigate();
  const [voice, setVoice] = useState(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef(null);
  const voiceId = weeklyVoiceId();

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/voices/${voiceId}/details`)
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(d => setVoice(d.voice))
      .catch(() => {});
    return () => { if (audioRef.current) audioRef.current.pause(); };
  }, [voiceId]);

  if (!voice) return null;

  const name = voice.name?.split(' - ')[0] || voice.name;
  const subtitle = voice.name?.split(' - ')[1] || '';
  const tags = [voice.gender, voice.age?.replace('_', ' '), voice.accent, voice.descriptive].filter(Boolean);

  const togglePlay = () => {
    if (audioRef.current && !audioRef.current.ended) {
      if (playing) { audioRef.current.pause(); setPlaying(false); }
      else { audioRef.current.play(); setPlaying(true); }
      return;
    }
    const audio = new Audio(`${BACKEND_URL}/api/voices/${voiceId}/sample`);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onerror = () => setPlaying(false);
    audio.play().then(() => setPlaying(true)).catch(() => {});
  };

  return (
    <section data-testid="votw-section" className="relative z-10 py-14 md:py-20" style={{ background: '#12161F', borderTop: '1px solid rgba(215,179,119,0.2)' }}>
      <div className="container mx-auto px-6" style={{ maxWidth: '960px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '40px' }}>

          {/* Portrait */}
          <div style={{ flex: '0 0 auto', margin: '0 auto', position: 'relative' }}>
            <img
              src={portraitManifest[voiceId]}
              alt={`${name} — VoxSmyth Voice of the Week`}
              loading="lazy"
              style={{
                width: '240px', height: '240px', objectFit: 'cover',
                borderRadius: '18px', display: 'block',
                border: '3px solid rgba(215,179,119,0.6)',
                boxShadow: '0 16px 48px rgba(0,0,0,0.5), 0 0 40px rgba(215,179,119,0.15)',
              }}
            />
            <div style={{
              position: 'absolute', top: '-14px', left: '50%', transform: 'translateX(-50%)',
              padding: '5px 16px', borderRadius: '14px', whiteSpace: 'nowrap',
              background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
              color: '#12161F', fontSize: '11px', fontWeight: 800,
              fontFamily: 'DM Sans, sans-serif', letterSpacing: '1.5px', textTransform: 'uppercase',
              boxShadow: '0 4px 12px rgba(215,179,119,0.4)',
            }}>
              Voice of the Week
            </div>
          </div>

          {/* Info */}
          <div style={{ flex: '1 1 380px' }}>
            <h2 data-testid="votw-name" style={{
              fontFamily: 'DM Serif Display, Georgia, serif',
              fontSize: 'clamp(28px, 4vw, 40px)', color: '#F1EDE3', margin: '0 0 4px', lineHeight: 1.1,
            }}>
              Meet {name}
            </h2>
            {subtitle && (
              <div style={{ fontSize: '15px', color: '#D7B377', fontStyle: 'italic', fontFamily: 'DM Sans, sans-serif', marginBottom: '12px' }}>
                {subtitle}
              </div>
            )}
            {tags.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                {tags.map(t => (
                  <span key={t} style={{
                    padding: '3px 12px', borderRadius: '12px', fontSize: '12px', fontWeight: 600,
                    background: 'rgba(215,179,119,0.12)', color: '#D7B377',
                    border: '1px solid rgba(215,179,119,0.3)', textTransform: 'capitalize',
                    fontFamily: 'DM Sans, sans-serif',
                  }}>
                    {t}
                  </span>
                ))}
              </div>
            )}
            <p style={{ fontSize: '15px', color: '#A0AEC0', lineHeight: 1.7, margin: '0 0 24px', fontFamily: 'Inter, sans-serif', maxWidth: '520px' }}>
              {voice.description || 'A professional AI narration voice.'} One of 65+ voices ready to bring your characters to life.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
              <button
                data-testid="votw-play"
                onClick={togglePlay}
                style={{
                  padding: '12px 26px', borderRadius: '28px', border: 'none',
                  background: 'linear-gradient(135deg, #D7B377 0%, #E2D7C4 100%)',
                  color: '#12161F', fontSize: '15px', fontWeight: 700, cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  fontFamily: 'DM Sans, sans-serif',
                  boxShadow: '0 4px 20px rgba(215,179,119,0.35)',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#12161F">
                  {playing
                    ? <><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></>
                    : <polygon points="5,3 19,12 5,21" />}
                </svg>
                {playing ? 'Pause' : `Hear ${name}`}
              </button>
              <button
                data-testid="votw-browse-link"
                onClick={() => navigate('/voices')}
                style={{
                  padding: '12px 26px', borderRadius: '28px',
                  border: '1px solid rgba(215,179,119,0.5)', background: 'transparent',
                  color: '#D7B377', fontSize: '15px', fontWeight: 600, cursor: 'pointer',
                  fontFamily: 'DM Sans, sans-serif', transition: 'background-color 0.2s',
                }}
              >
                Browse All 65 Voices
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VoiceOfTheWeek;
